import type { JSX } from 'react';
import { Box, Code, Paper, Text } from '@mantine/core';
import { type No, previaWhatsapp } from '../lib/previaWhatsapp.js';

/**
 * Renderiza a prévia a partir da ÁRVORE DE NÓS — nunca de HTML em string.
 *
 * `dangerouslySetInnerHTML` com texto que vem do banco seria XSS armazenado:
 * quem edita a FAQ poderia injetar script no painel de quem só lê. A migração
 * para Mantine trocou os contêineres, não isto: cada nó continua virando um
 * elemento React, e o texto continua sendo `children`, que o React escapa.
 */
function renderizar(nos: No[], chave = ''): JSX.Element[] {
  return nos.map((no, i) => {
    const k = `${chave}-${i}`;
    switch (no.tipo) {
      case 'texto':
        return <span key={k}>{no.valor}</span>;
      case 'negrito':
        return <strong key={k}>{renderizar(no.filhos, k)}</strong>;
      case 'italico':
        return <em key={k}>{renderizar(no.filhos, k)}</em>;
      case 'riscado':
        return <s key={k}>{renderizar(no.filhos, k)}</s>;
      case 'mono':
        return (
          <Code key={k} fz="inherit">
            {no.valor}
          </Code>
        );
    }
  });
}

/**
 * O CORPO da bolha: as linhas do texto, já em nós.
 *
 * Exportado porque o histórico da conversa monta a própria bolha (alinhamento,
 * horário, selo de não-enviado) mas precisa do MESMO renderizador. Duas cópias
 * desta função seriam duas chances de uma delas virar `innerHTML`.
 */
export function CorpoWhatsapp({ texto }: { texto: string }): JSX.Element {
  const linhas = previaWhatsapp(texto);
  return (
    <>
      {linhas.map((linha, i) => (
        <div key={i} className={linha.nos.length === 0 ? 'previa-vazia' : undefined}>
          {renderizar(linha.nos, String(i))}
        </div>
      ))}
    </>
  );
}

/**
 * `nota` troca a legenda. No editor, ela conta caracteres; no histórico de
 * atendimento, a mesma bolha mostra um texto que já foi (ou seria) enviado, e
 * "é assim que o aluno vê" ali seria a legenda errada.
 */
export function PreviaWhatsapp({
  texto,
  nota,
}: {
  texto: string;
  nota?: string | null;
}): JSX.Element {
  return (
    <Box my="xs">
      {/* A bolha mantém a cor do WhatsApp nos dois temas: é citação do que o
          aluno vê, não elemento de interface. Ver `.previa-bolha`. */}
      <Paper className="previa-bolha" p="sm" radius="md" maw={440}>
        <CorpoWhatsapp texto={texto} />
      </Paper>
      {nota !== null && (
        <Text size="xs" c="dimmed" mt={4}>
          {nota ?? `É assim que o aluno vê. ${texto.length} caracteres.`}
        </Text>
      )}
    </Box>
  );
}
